import { memo, useRef, useState, useEffect } from "react";
import ErrorBanner from "../../ImageGenerator/ErrorBanner/ErrorBanner";
import {
  normalizeStatus,
  isSuccessStatus,
  isInProgressStatus,
  extractPercentage,
} from "../utils";
import styles from "./AnimationStatusPanel.module.css";

function AnimationStatusPanel({
  statusData,
  errorMessage,
  onDismissError,
  videoUrl,
  isSubmitting,
  isJobProcessing,
  isPreparingPlayback,
}) {
  const normalizedStatus = normalizeStatus(statusData) || (statusData ? "" : "IDLE");
  const isCompleted = isSuccessStatus(normalizedStatus);
  const isInProgress = isInProgressStatus(normalizedStatus);
  const showPlayablePreview = Boolean(videoUrl);

  const prevIsSubmittingRef = useRef(false);
  const maxPercentageRef = useRef(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Reset tracked progress on new submissions
  if (isSubmitting && !prevIsSubmittingRef.current) {
    maxPercentageRef.current = 0;
  }
  prevIsSubmittingRef.current = isSubmitting;

  const rawPercentage = extractPercentage(statusData);
  if (rawPercentage !== null) {
    maxPercentageRef.current = Math.max(maxPercentageRef.current, rawPercentage);
  }

  // Count-up timer during processing
  useEffect(() => {
    let intervalId;
    if (isJobProcessing && !isPreparingPlayback && !isSubmitting) {
      const startTime = Date.now();
      setElapsedSeconds(0);
      intervalId = setInterval(() => {
        setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    } else {
      setElapsedSeconds(0);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isJobProcessing, isPreparingPlayback, isSubmitting]);

  const showLoadingPanel =
    isSubmitting || isJobProcessing || isPreparingPlayback;

  const getStatusLabel = () => {
    if (isSubmitting) {
      return "Submitting your request to the server...";
    }
    if (isPreparingPlayback) {
      return "Buffering preview and video for playback...";
    }
    if (isJobProcessing) {
      const timeSuffix = elapsedSeconds > 0 ? ` (${elapsedSeconds}s)` : "";
      if (isCompleted) {
        return `Finishing up...${timeSuffix}`;
      }
      if (normalizedStatus === "PENDING") {
        return `Preparing your animation...${timeSuffix}`;
      }
      if (isInProgress) {
        return `Generating animation on the server...${timeSuffix}`;
      }
      if (normalizedStatus === "IDLE") {
        return "";
      }
      return `${normalizedStatus || "Processing..."}${timeSuffix}`;
    }
    return "";
  };

  return (
    <div className={styles.middleContainer}>
      <div className={styles.middleblock}>
        {errorMessage ? (
          <ErrorBanner message={errorMessage} onDismiss={onDismissError} />
        ) : null}

        <div className={styles.previewContainer}>
          {showLoadingPanel ? (
            <div className={styles.loadingContent}>
              <div className={styles.statusInfo}>
                <p className={styles.statusLabel}>{getStatusLabel()}</p>
                {isJobProcessing && !isPreparingPlayback && !isSubmitting ? (
                  <div className={styles.progressTrack}>
                    <div className={styles.progressBarIndeterminate} />
                  </div>
                ) : null}
                <p className={styles.meta}>
                  This usually takes a few minutes. Progress updates appear here,
                  and the preview will show when your animation is ready.
                </p>
              </div>
            </div>
          ) : showPlayablePreview ? (
            <video controls src={videoUrl} className={styles.videoPlayer} />
          ) : (
            <div className={styles.previewPlaceholder}>
              {statusData && !isCompleted && !errorMessage ? (
                <p>Waiting for animation result...</p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(AnimationStatusPanel);
